import React, { useMemo } from "react";
import { Autocomplete, TextField } from "@mui/material";

const defaultGetValue = (option) =>
  option && typeof option === "object" ? option.value : option;
const defaultGetLabel = (option) =>
  option && typeof option === "object" ? option.label ?? "" : option ?? "";

// Autocomplete con la stessa interfaccia di una Select: riceve e restituisce
// il value (o un array di value con `multiple`), non l'opzione intera.
// I value si confrontano come stringhe perche' le API mescolano id numerici e
// stringa. Come una Select, di default non si puo' svuotare (disableClearable)
const AutocompleteSelect = ({
  options = [],
  value,
  onChange,
  label,
  multiple = false,
  getValue = defaultGetValue,
  getLabel = defaultGetLabel,
  error,
  helperText,
  required,
  placeholder,
  size = "small",
  fullWidth = true,
  disableClearable = !multiple,
  textFieldProps,
  ...props
}) => {
  const sameValue = (a, b) =>
    a !== undefined && a !== null && b !== undefined && b !== null
      ? String(a) === String(b)
      : a === b;

  const selected = useMemo(() => {
    if (multiple) {
      const values = Array.isArray(value) ? value : [];
      return options.filter((o) =>
        values.some((v) => sameValue(getValue(o), v)),
      );
    }
    return options.find((o) => sameValue(getValue(o), value)) ?? null;
  }, [options, value, multiple, getValue]);

  return (
    <Autocomplete
      size={size}
      fullWidth={fullWidth}
      multiple={multiple}
      options={options}
      value={selected}
      disableClearable={disableClearable}
      getOptionLabel={(o) => String(getLabel(o) ?? "")}
      isOptionEqualToValue={(o, v) => sameValue(getValue(o), getValue(v))}
      onChange={(_, newValue) => {
        if (multiple) onChange?.((newValue || []).map(getValue));
        else onChange?.(newValue ? getValue(newValue) : "");
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          placeholder={placeholder}
          error={Boolean(error)}
          helperText={helperText}
          {...textFieldProps}
        />
      )}
      {...props}
    />
  );
};

export default AutocompleteSelect;
